import { useState, useEffect } from 'react'
import { formatearPeso, formatearFecha, formatearHora } from '../../utils/formato'
import { obtenerMovimientosCliente, registrarAbono } from '../../db/ventas'
import { guardarCliente } from '../../db/clientes'
import { useConfig } from '../../context/ConfigContext'

const TECLAS = [['7', '8', '9'], ['4', '5', '6'], ['1', '2', '3'], ['.', '0', '⌫']]

const METODOS_PAGO = [
  {
    id: 'efectivo',
    label: 'Efectivo',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <circle cx="12" cy="12" r="3" />
        <path d="M6 12h.01M18 12h.01" />
      </svg>
    ),
  },
  {
    id: 'tarjeta',
    label: 'Tarjeta',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M2 10h20M6 15h4" />
      </svg>
    ),
  },
  {
    id: 'transferencia',
    label: 'Transfer.',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M8 6l4-4 4 4M16 18l-4 4-4-4M12 2v20" />
      </svg>
    ),
  },
]

const DOT_METODO = { efectivo: 'bg-tienda-success', tarjeta: 'bg-blue-400', transferencia: 'bg-purple-400' }
const LABEL_METODO = { efectivo: 'Efectivo', tarjeta: 'Tarjeta', transferencia: 'Transf.' }

function generarMontosRapidos(total) {
  const candidatos = []
  const billetes = [10, 20, 50, 100, 200, 500, 1000]
  for (const b of billetes) {
    const multiplo = Math.ceil(total / b) * b
    if (multiplo >= total && !candidatos.includes(multiplo)) candidatos.push(multiplo)
    if (candidatos.length >= 4) break
  }
  return candidatos.slice(0, 4)
}

// Distribuye abonos contra ventas en orden FIFO (deuda más antigua primero)
function distribuirAbonos(movimientos) {
  const ventas = movimientos
    .filter(m => m.tipo === 'venta')
    .sort((a, b) => a.fecha.localeCompare(b.fecha))
    .map(v => ({ ...v, saldoPendiente: v.total, abonosAsignados: [] }))

  const abonos = movimientos
    .filter(m => m.tipo === 'abono')
    .sort((a, b) => a.fecha.localeCompare(b.fecha))

  for (const abono of abonos) {
    let restante = abono.monto
    for (const venta of ventas) {
      if (venta.saldoPendiente <= 0 || restante <= 0) continue
      const aplicado = Math.min(venta.saldoPendiente, restante)
      venta.saldoPendiente = Math.max(0, +(venta.saldoPendiente - aplicado).toFixed(2))
      venta.abonosAsignados.push({ ...abono, aplicado: +aplicado.toFixed(2) })
      restante = +(restante - aplicado).toFixed(2)
    }
  }

  return ventas.reverse() // más reciente primero
}

export default function ModalDetalleCliente({ cliente, onCerrar, onActualizado }) {
  const { config } = useConfig()
  const [vista, setVista] = useState('detalle')   // detalle | editar | pago | ticket
  const [editandoMonto, setEditandoMonto] = useState(false)
  const [movimientos, setMovimientos] = useState([])
  const [ventaDetalle, setVentaDetalle] = useState(null)

  const [nombre, setNombre] = useState(cliente.nombre)
  const [telefono, setTelefono] = useState(cliente.telefono || '')

  const [monto, setMonto] = useState('')
  const [metodoPago, setMetodoPago] = useState('efectivo')
  const [recibido, setRecibido] = useState('')
  const [procesando, setProcesando] = useState(false)

  useEffect(() => {
    obtenerMovimientosCliente(cliente._id).then(setMovimientos)
  }, [cliente._id])

  function abrirPago() {
    const deudaStr = cliente.deuda % 1 === 0 ? String(cliente.deuda) : cliente.deuda.toFixed(2)
    setMonto(deudaStr)
    setMetodoPago('efectivo')
    setRecibido('')
    setEditandoMonto(false)
    setVista('pago')
  }

  function abrirTicket(venta) {
    setVentaDetalle(venta)
    setVista('ticket')
  }

  useEffect(() => { setRecibido('') }, [metodoPago])

  function presionarMonto(tecla) {
    if (tecla === '⌫') { setMonto(v => v.slice(0, -1)); return }
    if (tecla === '.' && monto.includes('.')) return
    if (tecla === '.' && monto === '') { setMonto('0.'); return }
    const partes = monto.split('.')
    if (partes.length === 2 && partes[1].length >= 2) return
    if (partes[0].length >= 6 && tecla !== '.') return
    setMonto(v => v + tecla)
  }

  function presionarRecibido(tecla) {
    if (tecla === '⌫') { setRecibido(v => v.slice(0, -1)); return }
    if (tecla === '.' && recibido.includes('.')) return
    if (tecla === '.' && recibido === '') { setRecibido('0.'); return }
    const partes = recibido.split('.')
    if (partes.length === 2 && partes[1].length >= 2) return
    if (partes[0].length >= 6 && tecla !== '.') return
    setRecibido(v => v + tecla)
  }

  const montoNum = parseFloat(monto) || 0
  const montoValido = montoNum > 0 && montoNum <= (cliente.deuda || 0)
  const recibidoNum = parseFloat(recibido) || 0
  const cambio = recibidoNum - montoNum
  const alcanza = recibidoNum >= montoNum
  const puedeConfirmar = montoValido && (metodoPago !== 'efectivo' || alcanza)

  async function handleGuardarEdicion() {
    if (!nombre.trim() || procesando) return
    setProcesando(true)
    await guardarCliente({ ...cliente, nombre: nombre.trim(), telefono: telefono.trim() })
    await onActualizado()
    setProcesando(false)
    onCerrar()
  }

  async function handleConfirmarPago() {
    if (!puedeConfirmar || procesando) return
    setProcesando(true)
    await registrarAbono(cliente._id, cliente.nombre, montoNum, {
      metodoPago,
      recibido: metodoPago === 'efectivo' ? recibidoNum : null,
      cambio: metodoPago === 'efectivo' ? cambio : null,
    })
    await onActualizado()
    setProcesando(false)
    onCerrar()
  }

  const ventasConAbonos = distribuirAbonos(movimientos)

  const tituloHeader = {
    detalle: cliente.nombre,
    editar: 'Editar cliente',
    pago: 'Registrar pago',
    ticket: 'Ticket',
  }[vista]

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-tienda-surface w-full max-w-lg rounded-t-3xl overflow-hidden flex flex-col max-h-[90vh]">

        {/* Cabecera */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 flex-shrink-0 border-b border-tienda-border">
          <div className="flex items-center gap-3">
            {vista !== 'detalle' && (
              <button onClick={() => setVista('detalle')} className="text-tienda-muted">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M19 12H5M5 12l7-7M5 12l7 7" />
                </svg>
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold text-tienda-text">{tituloHeader}</h2>
              {vista === 'detalle' && cliente.telefono && (
                <p className="text-tienda-muted text-sm">{cliente.telefono}</p>
              )}
              {vista === 'ticket' && ventaDetalle && (
                <p className="text-tienda-muted text-sm">{formatearFecha(ventaDetalle.fecha)}</p>
              )}
            </div>
          </div>
          <button onClick={onCerrar} className="w-9 h-9 flex items-center justify-center text-tienda-muted">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">

          {/* ── DETALLE ── */}
          {vista === 'detalle' && (
            <div className="p-5 flex flex-col gap-4">
              {/* Card deuda */}
              <div className={`rounded-2xl p-4 flex items-center justify-between
                ${(cliente.deuda || 0) > 0
                  ? 'bg-tienda-danger/10 border border-tienda-danger/30'
                  : 'bg-tienda-success/10 border border-tienda-success/30'}`}>
                <span className={`font-semibold ${(cliente.deuda || 0) > 0 ? 'text-tienda-danger' : 'text-tienda-success'}`}>
                  {(cliente.deuda || 0) > 0 ? 'Deuda actual' : 'Sin deuda'}
                </span>
                <span className={`text-3xl font-bold ${(cliente.deuda || 0) > 0 ? 'text-tienda-danger' : 'text-tienda-success'}`}>
                  {formatearPeso(cliente.deuda || 0)}
                </span>
              </div>

              <div className="flex gap-2">
                {(cliente.deuda || 0) > 0 && (
                  <button onClick={abrirPago} className="flex-1 btn-primary text-center text-base py-3">
                    Registrar pago
                  </button>
                )}
                <button onClick={() => setVista('editar')} className="flex-1 btn-ghost text-center">
                  Editar
                </button>
              </div>

              {/* Historial agrupado */}
              {ventasConAbonos.length > 0 && (
                <div>
                  <p className="text-tienda-muted text-xs font-semibold uppercase tracking-wider mb-2">
                    Historial
                  </p>
                  <div className="space-y-3">
                    {ventasConAbonos.map(venta => (
                      <VentaConAbonos
                        key={venta._id}
                        venta={venta}
                        onVerTicket={() => abrirTicket(venta)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {movimientos.length === 0 && (cliente.deuda || 0) === 0 && (
                <p className="text-tienda-muted text-sm text-center py-4">Sin movimientos registrados</p>
              )}
            </div>
          )}

          {/* ── EDITAR ── */}
          {vista === 'editar' && (
            <div className="p-5 flex flex-col gap-4">
              <input
                autoFocus
                className="input-field"
                placeholder="Nombre *"
                value={nombre}
                onChange={e => setNombre(e.target.value)}
              />
              <input
                className="input-field"
                placeholder="Teléfono (10 dígitos)"
                value={telefono}
                onChange={e => setTelefono(e.target.value.replace(/\D/g, '').slice(0, 10))}
                type="tel"
                inputMode="numeric"
                maxLength={10}
              />
            </div>
          )}

          {/* ── TICKET ── */}
          {vista === 'ticket' && ventaDetalle && (
            <div className="p-5">
              <div className="bg-tienda-bg rounded-2xl p-4 font-mono text-sm space-y-1">
                <div className="text-center mb-3">
                  <p className="text-tienda-text font-bold text-base">{config.nombreTienda}</p>
                  <p className="text-tienda-muted text-xs">Folio: PJ-{ventaDetalle._id.slice(-6).toUpperCase()}</p>
                </div>
                <div className="border-t border-dashed border-tienda-border pt-2 space-y-1">
                  {ventaDetalle.items?.map(item => (
                    <div key={item._id} className="flex justify-between gap-2">
                      <span className="text-tienda-muted truncate">
                        {item.unidad === 'kg' ? `${item.cantidad}kg` : `${item.cantidad}x`} {item.nombre}
                      </span>
                      <span className="text-tienda-text flex-shrink-0">
                        {formatearPeso(item.unidad === 'kg' ? item.precio : item.precio * item.cantidad)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-dashed border-tienda-border pt-2 flex justify-between">
                  <span className="font-bold text-tienda-text">TOTAL</span>
                  <span className="font-bold text-tienda-danger text-base">{formatearPeso(ventaDetalle.total)}</span>
                </div>
                <div className="border-t border-dashed border-tienda-border pt-2 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-tienda-muted">Forma de pago</span>
                    <span className="text-tienda-danger font-semibold">Fiado</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-tienda-muted">Cliente</span>
                    <span className="text-tienda-text font-semibold">{ventaDetalle.clienteNombre}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-tienda-muted">Hora</span>
                    <span className="text-tienda-text">{formatearHora(ventaDetalle.fecha)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── PAGO ── */}
          {vista === 'pago' && (
            <div className="p-5 flex flex-col gap-4">
              <button
                onClick={() => setEditandoMonto(v => !v)}
                className={`w-full rounded-2xl border p-4 text-left transition-colors
                  ${editandoMonto
                    ? 'bg-tienda-accent/5 border-tienda-accent'
                    : 'bg-tienda-bg border-tienda-border'}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-tienda-muted text-sm">Monto a abonar</p>
                  <span className={`text-xs font-semibold ${editandoMonto ? 'text-tienda-accent' : 'text-tienda-muted'}`}>
                    {editandoMonto ? 'Listo ✓' : 'Editar ✏️'}
                  </span>
                </div>
                <p className={`text-4xl font-bold tracking-tight
                  ${montoNum > 0
                    ? montoNum <= (cliente.deuda || 0) ? 'text-tienda-accent' : 'text-tienda-danger'
                    : 'text-tienda-border'}`}>
                  {monto ? `$${monto}` : '$0.00'}
                </p>
                <p className="text-tienda-muted text-xs mt-1">
                  Deuda total: <span className="text-tienda-danger font-semibold">{formatearPeso(cliente.deuda)}</span>
                </p>
              </button>

              {editandoMonto && (
                <div className="grid grid-cols-3 gap-2">
                  {TECLAS.flat().map(tecla => (
                    <button
                      key={tecla}
                      onPointerDown={e => { e.preventDefault(); presionarMonto(tecla) }}
                      className={`h-14 rounded-2xl text-xl font-bold flex items-center justify-center
                        active:scale-95 transition-transform duration-75 select-none
                        ${tecla === '⌫'
                          ? 'bg-tienda-danger/20 text-tienda-danger border border-tienda-danger/30'
                          : 'bg-tienda-bg border border-tienda-border text-tienda-text active:bg-tienda-border'}`}
                    >
                      {tecla}
                    </button>
                  ))}
                </div>
              )}

              {!editandoMonto && (
                <>
                  <div className="flex gap-2">
                    {METODOS_PAGO.map(m => (
                      <button
                        key={m.id}
                        onClick={() => setMetodoPago(m.id)}
                        className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-2xl border transition-colors
                          ${metodoPago === m.id
                            ? 'bg-tienda-accent/10 border-tienda-accent text-tienda-accent'
                            : 'bg-tienda-bg border-tienda-border text-tienda-muted'}`}
                      >
                        {m.icon}
                        <span className="text-xs font-semibold">{m.label}</span>
                      </button>
                    ))}
                  </div>

                  {metodoPago === 'efectivo' && (
                    <>
                      <div className="bg-tienda-bg rounded-2xl border border-tienda-border p-4">
                        <p className="text-tienda-muted text-sm mb-1">Importe recibido</p>
                        <p className={`text-4xl font-bold tracking-tight min-h-[2.5rem]
                          ${recibido ? (alcanza ? 'text-tienda-success' : 'text-tienda-danger') : 'text-tienda-border'}`}>
                          {recibido ? `$${recibido}` : '$0.00'}
                        </p>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        {generarMontosRapidos(montoNum).map(v => (
                          <button
                            key={v}
                            onClick={() => setRecibido(String(v))}
                            className={`flex-1 min-w-[4rem] py-2.5 rounded-xl text-base font-bold border transition-colors
                              ${recibidoNum === v
                                ? 'bg-tienda-accent text-gray-900 border-tienda-accent'
                                : 'bg-tienda-bg border-tienda-border text-tienda-text active:bg-tienda-border'}`}
                          >
                            ${v}
                          </button>
                        ))}
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        {TECLAS.flat().map(tecla => (
                          <button
                            key={tecla}
                            onPointerDown={e => { e.preventDefault(); presionarRecibido(tecla) }}
                            className={`h-14 rounded-2xl text-xl font-bold flex items-center justify-center
                              active:scale-95 transition-transform duration-75 select-none
                              ${tecla === '⌫'
                                ? 'bg-tienda-danger/20 text-tienda-danger border border-tienda-danger/30'
                                : 'bg-tienda-bg border border-tienda-border text-tienda-text active:bg-tienda-border'}`}
                          >
                            {tecla}
                          </button>
                        ))}
                      </div>

                      {recibido !== '' && (
                        <div className={`rounded-2xl p-4 flex items-center justify-between
                          ${alcanza ? 'bg-tienda-success/10 border border-tienda-success/30' : 'bg-tienda-danger/10 border border-tienda-danger/30'}`}>
                          <span className={`text-base font-semibold ${alcanza ? 'text-tienda-success' : 'text-tienda-danger'}`}>
                            {alcanza ? 'Cambio a devolver' : 'Monto insuficiente'}
                          </span>
                          <span className={`text-2xl font-bold ${alcanza ? 'text-tienda-success' : 'text-tienda-danger'}`}>
                            {alcanza ? formatearPeso(cambio) : `-${formatearPeso(Math.abs(cambio))}`}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {(metodoPago === 'tarjeta' || metodoPago === 'transferencia') && (
                    <div className="bg-tienda-bg rounded-2xl border border-tienda-border p-6 flex flex-col items-center text-center gap-3">
                      <span className="text-5xl">{metodoPago === 'tarjeta' ? '💳' : '📲'}</span>
                      <p className="text-tienda-muted text-sm leading-relaxed">
                        {metodoPago === 'tarjeta'
                          ? 'Procesa el cobro en tu terminal bancaria.'
                          : 'Confirma cuando recibas la transferencia.'}
                      </p>
                      <div className="w-full border-t border-tienda-border pt-3">
                        <p className="text-tienda-muted text-sm">Abono</p>
                        <p className="text-3xl font-bold text-tienda-success mt-1">{formatearPeso(montoNum)}</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Botones inferiores */}
        <div className={`flex gap-3 p-5 border-t border-tienda-border flex-shrink-0 ${vista === 'ticket' || vista === 'detalle' ? 'hidden' : ''}`}>
          <button onClick={onCerrar} className="btn-ghost flex-1 text-center">
            Cancelar
          </button>

          {vista === 'editar' && (
            <button
              onClick={handleGuardarEdicion}
              disabled={!nombre.trim() || procesando}
              className="flex-1 btn-primary text-center text-base"
              style={{ opacity: !nombre.trim() ? 0.5 : 1 }}
            >
              {procesando ? 'Guardando...' : 'Guardar'}
            </button>
          )}

          {vista === 'pago' && !editandoMonto && (
            <button
              onClick={handleConfirmarPago}
              disabled={!puedeConfirmar || procesando}
              className={`flex-1 text-center text-base font-bold rounded-2xl px-6 py-4 transition-all
                ${puedeConfirmar && !procesando
                  ? 'btn-primary'
                  : 'bg-tienda-surface border border-tienda-border text-tienda-muted cursor-not-allowed'}`}
            >
              {procesando ? 'Registrando...' : 'Confirmar pago'}
            </button>
          )}

          {vista === 'pago' && editandoMonto && (
            <button
              onClick={() => setEditandoMonto(false)}
              disabled={!montoValido}
              className={`flex-1 text-center text-base font-bold rounded-2xl px-6 py-4 transition-all
                ${montoValido ? 'btn-primary' : 'bg-tienda-surface border border-tienda-border text-tienda-muted cursor-not-allowed'}`}
            >
              Listo
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Venta con abonos anidados ─────────────────────────────────────
function VentaConAbonos({ venta, onVerTicket }) {
  const saldada = venta.saldoPendiente <= 0

  return (
    <div className={`rounded-2xl border overflow-hidden ${saldada ? 'border-tienda-success/30' : 'border-tienda-danger/30'}`}>
      {/* Cabecera de venta — tappable */}
      <button
        onClick={onVerTicket}
        className={`w-full flex items-center justify-between px-4 py-3 text-left active:brightness-90 transition-colors
          ${saldada ? 'bg-tienda-success/10' : 'bg-tienda-danger/10'}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-lg flex-shrink-0">🛒</span>
          <div className="min-w-0">
            <p className="text-tienda-text text-sm font-semibold">
              {venta.items?.length ?? 0} artículo{(venta.items?.length ?? 0) !== 1 ? 's' : ''}
            </p>
            <p className="text-tienda-muted text-xs">{formatearFecha(venta.fecha)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            <p className={`font-bold text-base ${saldada ? 'text-tienda-success' : 'text-tienda-danger'}`}>
              {formatearPeso(venta.total)}
            </p>
            {!saldada && (
              <p className="text-tienda-danger text-xs">
                Pendiente: {formatearPeso(venta.saldoPendiente)}
              </p>
            )}
            {saldada && (
              <p className="text-tienda-success text-xs font-semibold">Saldado ✓</p>
            )}
          </div>
          <svg className="w-4 h-4 text-tienda-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </div>
      </button>

      {/* Abonos asignados */}
      {venta.abonosAsignados.length > 0 && (
        <div className="divide-y divide-tienda-border/50">
          {venta.abonosAsignados.map((abono, i) => (
            <div key={`${abono._id}-${i}`} className="flex items-center justify-between px-4 py-2.5 bg-tienda-bg/50">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${DOT_METODO[abono.metodoPago] ?? 'bg-tienda-muted'}`} />
                <div className="min-w-0">
                  <p className="text-tienda-text text-xs font-semibold">
                    Abono · {LABEL_METODO[abono.metodoPago] ?? abono.metodoPago}
                  </p>
                  <p className="text-tienda-muted text-xs">{formatearFecha(abono.fecha)}</p>
                </div>
              </div>
              <span className="text-tienda-success font-bold text-sm flex-shrink-0">
                -{formatearPeso(abono.aplicado)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
